import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { appInsights, isAppInsightsStarted } from './appInsights';

/**
 * Application Insights telemetry wrapper for the Maintenance frontend.
 * Single entry point the rest of the app uses to send telemetry: screens and
 * hooks import trackException/trackTrace/trackEvent/trackMetric from here
 * instead of calling the `appInsights` SDK instance (appInsights.ts)
 * directly, so the SDK call shape lives in one place.
 *
 * Every helper is a no-op until startAppInsights() has actually loaded the SDK
 * (enableTelemetry flag on and a connection string configured). Tracking
 * before that would skip the telemetry initializers (source/screen/operation
 * ID) or send telemetry while the flag is off. SDK errors are caught and
 * logged, so telemetry can never break the screen that called it.
 */

export { SeverityLevel };
export { startAppInsights, isAppInsightsStarted } from './appInsights';

export type TelemetryProperties = Record<string, unknown>;

/**
 * Converts a caught value into an Error. catch blocks hand us `unknown`, but
 * App Insights needs an Error to build a useful exception record.
 * @param error the caught value
 * @returns the value itself if it is already an Error; otherwise a new Error
 * built from its `message` string, or from its string form
 */
const toError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error;
  }
  if (
    error &&
    typeof error === 'object' &&
    typeof (error as { message?: unknown }).message === 'string'
  ) {
    return new Error((error as { message: string }).message);
  }
  return new Error(String(error));
};

/**
 * Runs one SDK call only while App Insights is started, and never lets an SDK
 * failure escape: the track helpers are called from catch blocks and render
 * paths all over the app, so a telemetry error must not break the screen that
 * called it (same reasoning as the try/catch in startAppInsights).
 * @param method SDK method name, used in the warning if the call throws
 * @param send the SDK call to make
 */
const sendSafely = (method: string, send: () => void): void => {
  if (!isAppInsightsStarted()) return;
  try {
    send();
  } catch (err) {
    console.warn(`App Insights ${method} failed.`, err);
  }
};

/**
 * Reports an exception to Application Insights.
 * @param error the caught error (non-Error values are wrapped in an Error)
 * @param properties extra custom properties to attach
 */
export const trackException = (
  error: unknown,
  properties?: TelemetryProperties,
): void => {
  sendSafely('trackException', () =>
    appInsights.trackException({ exception: toError(error), properties }),
  );
};

/**
 * Reports a free-form trace message at the given severity level.
 * @param message the trace message
 * @param severityLevel defaults to Information
 * @param properties extra custom properties to attach
 */
export const trackTrace = (
  message: string,
  severityLevel: SeverityLevel = SeverityLevel.Information,
  properties?: TelemetryProperties,
): void => {
  sendSafely('trackTrace', () =>
    appInsights.trackTrace({ message, severityLevel }, properties),
  );
};

/**
 * Reports a named custom event.
 * @param name the event name
 * @param properties extra custom properties to attach
 */
export const trackEvent = (
  name: string,
  properties?: TelemetryProperties,
): void => {
  sendSafely('trackEvent', () => appInsights.trackEvent({ name }, properties));
};

/**
 * Reports a custom metric value (e.g. a web-vitals measurement).
 * @param name the metric name
 * @param value the measured value
 * @param properties extra custom properties to attach
 */
export const trackMetric = (
  name: string,
  value: number,
  properties?: TelemetryProperties,
): void => {
  sendSafely('trackMetric', () =>
    appInsights.trackMetric({ name, average: value }, properties),
  );
};
